import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Condition {
  metric: string
  operator: string
  threshold: number
  time_window?: string
}

interface AlertRule {
  id: string
  name: string
  description?: string
  conditions: Condition[]
  condition_logic: string
  severity: string
  enabled: boolean
  notification_channels: string[]
  cooldown_minutes: number
  tags: string[]
  created_at: string
  last_triggered?: string
  trigger_count: number
}

interface Alert {
  id: string
  rule_id: string
  rule_name: string
  status: string
  severity: string
  triggered_value: number
  triggered_at: string
  acknowledged_at?: string
  resolved_at?: string
  context?: Record<string, any>
}

interface NotificationChannel {
  id: string
  name: string
  type: string
  config: Record<string, any>
  enabled: boolean
}

interface Metric {
  name: string
  label: string
  unit: string
  description: string
}

interface Operator {
  value: string
  label: string
}

const AlertRulesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'rules' | 'alerts' | 'channels' | 'history'>('rules')
  const [rules, setRules] = useState<AlertRule[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [channels, setChannels] = useState<NotificationChannel[]>([])
  const [history, setHistory] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [operators, setOperators] = useState<Operator[]>([])
  const [loading, setLoading] = useState(true)
  const [showRuleModal, setShowRuleModal] = useState(false)
  const [showChannelModal, setShowChannelModal] = useState(false)
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_editingChannel, _setEditingChannel] = useState<NotificationChannel | null>(null)

  // Form state
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    conditions: [{ metric: 'revenue', operator: '>', threshold: 0, time_window: '1h' }],
    condition_logic: 'AND',
    severity: 'medium',
    notification_channels: [] as string[],
    cooldown_minutes: 60,
    tags: [] as string[]
  })

  const [channelForm, setChannelForm] = useState({
    name: '',
    type: 'email',
    config: {} as Record<string, any>
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [rulesRes, alertsRes, channelsRes, historyRes, statsRes, metricsRes, operatorsRes] = await Promise.all([
        fetch('/api/alert-rules/rules'),
        fetch('/api/alert-rules/alerts'),
        fetch('/api/alert-rules/channels'),
        fetch('/api/alert-rules/history'),
        fetch('/api/alert-rules/statistics'),
        fetch('/api/alert-rules/metrics'),
        fetch('/api/alert-rules/operators')
      ])

      if (rulesRes.ok) {
        const data = await rulesRes.json()
        setRules(data.rules || [])
      }
      if (alertsRes.ok) {
        const data = await alertsRes.json()
        setAlerts(data.alerts || [])
      }
      if (channelsRes.ok) {
        const data = await channelsRes.json()
        setChannels(data.channels || [])
      }
      if (historyRes.ok) {
        const data = await historyRes.json()
        setHistory(data.history || [])
      }
      if (statsRes.ok) {
        const data = await statsRes.json()
        setStatistics(data)
      }
      if (metricsRes.ok) {
        const data = await metricsRes.json()
        setMetrics(data.metrics || [])
      }
      if (operatorsRes.ok) {
        const data = await operatorsRes.json()
        setOperators(data.operators || [])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    setLoading(false)
  }

  const handleCreateRule = async () => {
    try {
      const res = await fetch('/api/alert-rules/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ruleForm)
      })
      if (res.ok) {
        setShowRuleModal(false)
        resetRuleForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error creating rule:', error)
    }
  }

  const handleUpdateRule = async () => {
    if (!editingRule) return
    try {
      const res = await fetch(`/api/alert-rules/rules/${editingRule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ruleForm)
      })
      if (res.ok) {
        setShowRuleModal(false)
        setEditingRule(null)
        resetRuleForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error updating rule:', error)
    }
  }

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return
    try {
      const res = await fetch(`/api/alert-rules/rules/${ruleId}`, { method: 'DELETE' })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting rule:', error)
    }
  }

  const handleToggleRule = async (ruleId: string) => {
    try {
      const res = await fetch(`/api/alert-rules/rules/${ruleId}/toggle`, { method: 'POST' })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error toggling rule:', error)
    }
  }

  const handleTestRule = async (ruleId: string) => {
    try {
      const res = await fetch(`/api/alert-rules/rules/${ruleId}/test`, { method: 'POST' })
      if (res.ok) {
        alert('Test alert triggered!')
        fetchData()
      }
    } catch (error) {
      console.error('Error testing rule:', error)
    }
  }

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      const res = await fetch(`/api/alert-rules/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acknowledged_by: 'Current User' })
      })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error)
    }
  }

  const handleResolveAlert = async (alertId: string) => {
    try {
      const res = await fetch(`/api/alert-rules/alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved_by: 'Current User', resolution_notes: 'Resolved via UI' })
      })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error resolving alert:', error)
    }
  }

  const handleCreateChannel = async () => {
    try {
      const res = await fetch('/api/alert-rules/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(channelForm)
      })
      if (res.ok) {
        setShowChannelModal(false)
        resetChannelForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error creating channel:', error)
    }
  }

  const handleDeleteChannel = async (channelId: string) => {
    if (!confirm('Are you sure you want to delete this channel?')) return
    try {
      const res = await fetch(`/api/alert-rules/channels/${channelId}`, { method: 'DELETE' })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting channel:', error)
    }
  }

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      conditions: [{ metric: 'revenue', operator: '>', threshold: 0, time_window: '1h' }],
      condition_logic: 'AND',
      severity: 'medium',
      notification_channels: [],
      cooldown_minutes: 60,
      tags: []
    })
  }

  const resetChannelForm = () => {
    setChannelForm({
      name: '',
      type: 'email',
      config: {}
    })
  }

  const openEditRule = (rule: AlertRule) => {
    setEditingRule(rule)
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      conditions: rule.conditions.map(c => ({
        metric: c.metric,
        operator: c.operator,
        threshold: c.threshold,
        time_window: c.time_window || '1h'
      })),
      condition_logic: rule.condition_logic,
      severity: rule.severity,
      notification_channels: rule.notification_channels,
      cooldown_minutes: rule.cooldown_minutes,
      tags: rule.tags
    })
    setShowRuleModal(true)
  }

  const addCondition = () => {
    setRuleForm({
      ...ruleForm,
      conditions: [...ruleForm.conditions, { metric: 'revenue', operator: '>', threshold: 0, time_window: '1h' }]
    })
  }

  const removeCondition = (index: number) => {
    setRuleForm({
      ...ruleForm,
      conditions: ruleForm.conditions.filter((_, i) => i !== index)
    })
  }

  const updateCondition = (index: number, field: string, value: any) => {
    const newConditions = [...ruleForm.conditions]
    newConditions[index] = { ...newConditions[index], [field]: value }
    setRuleForm({ ...ruleForm, conditions: newConditions })
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc3545'
      case 'high': return '#fd7e14'
      case 'medium': return '#ffc107'
      case 'low': return '#28a745'
      default: return '#6c757d'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#dc3545'
      case 'acknowledged': return '#ffc107'
      case 'resolved': return '#28a745'
      default: return '#6c757d'
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px' }}>
            Alert Rules Engine
          </h1>
          <p style={{ color: '#64748b' }}>
            Configure alert rules and receive notifications when conditions are met
          </p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Total Rules</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e' }}>{statistics.total_rules}</div>
              <div style={{ fontSize: '12px', color: '#28a745' }}>{statistics.enabled_rules} enabled</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Active Alerts</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc3545' }}>{statistics.active_alerts}</div>
              <div style={{ fontSize: '12px', color: '#ffc107' }}>{statistics.acknowledged_alerts} acknowledged</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Notifications</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#4A90E2' }}>{statistics.total_channels}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{statistics.enabled_channels} active channels</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Alert History</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#7B68EE' }}>{statistics.history_count}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>events recorded</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
          {(['rules', 'alerts', 'channels', 'history'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: activeTab === tab ? '#4A90E2' : 'transparent',
                color: activeTab === tab ? 'white' : '#64748b',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                textTransform: 'capitalize'
              }}
            >
              {tab}
              {tab === 'alerts' && alerts.filter(a => a.status === 'active').length > 0 && (
                <span style={{
                  marginLeft: '8px',
                  background: '#dc3545',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px'
                }}>
                  {alerts.filter(a => a.status === 'active').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading...</div>
        ) : (
          <>
            {/* Rules Tab */}
            {activeTab === 'rules' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>Alert Rules</h2>
                  <button
                    onClick={() => { resetRuleForm(); setEditingRule(null); setShowRuleModal(true); }}
                    style={{
                      padding: '10px 20px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    + Create Rule
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {rules.map(rule => (
                    <div
                      key={rule.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${getSeverityColor(rule.severity)}`,
                        opacity: rule.enabled ? 1 : 0.6
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>{rule.name}</h3>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500,
                              background: getSeverityColor(rule.severity) + '20',
                              color: getSeverityColor(rule.severity)
                            }}>
                              {rule.severity}
                            </span>
                            {!rule.enabled && (
                              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', background: '#e2e8f0', color: '#64748b' }}>
                                Disabled
                              </span>
                            )}
                          </div>
                          {rule.description && (
                            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '12px' }}>{rule.description}</p>
                          )}
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            <strong>Conditions ({rule.condition_logic}):</strong>
                            {rule.conditions.map((c, i) => (
                              <span key={i} style={{ marginLeft: '8px', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>
                                {c.metric} {c.operator} {c.threshold}
                              </span>
                            ))}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                            Triggered {rule.trigger_count} times | Cooldown: {rule.cooldown_minutes}m
                            {rule.last_triggered && ` | Last: ${new Date(rule.last_triggered).toLocaleString()}`}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleToggleRule(rule.id)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #e2e8f0',
                              background: 'white',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            {rule.enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleTestRule(rule.id)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #4A90E2',
                              background: 'white',
                              color: '#4A90E2',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Test
                          </button>
                          <button
                            onClick={() => openEditRule(rule)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #e2e8f0',
                              background: 'white',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #dc3545',
                              background: 'white',
                              color: '#dc3545',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {rules.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px' }}>
                      No alert rules configured. Create your first rule to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Active Alerts</h2>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {alerts.map(alert => (
                    <div
                      key={alert.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${getStatusColor(alert.status)}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 600 }}>{alert.rule_name}</span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: getStatusColor(alert.status) + '20',
                              color: getStatusColor(alert.status),
                              fontWeight: 500
                            }}>
                              {alert.status}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: getSeverityColor(alert.severity) + '20',
                              color: getSeverityColor(alert.severity)
                            }}>
                              {alert.severity}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            Triggered value: {alert.triggered_value} | {new Date(alert.triggered_at).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {alert.status === 'active' && (
                            <button
                              onClick={() => handleAcknowledgeAlert(alert.id)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #ffc107',
                                background: 'white',
                                color: '#ffc107',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              Acknowledge
                            </button>
                          )}
                          {alert.status !== 'resolved' && (
                            <button
                              onClick={() => handleResolveAlert(alert.id)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #28a745',
                                background: 'white',
                                color: '#28a745',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              Resolve
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {alerts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px' }}>
                      No alerts at this time. Your alert rules are monitoring for issues.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Channels Tab */}
            {activeTab === 'channels' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>Notification Channels</h2>
                  <button
                    onClick={() => { resetChannelForm(); setShowChannelModal(true); }}
                    style={{
                      padding: '10px 20px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    + Add Channel
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {channels.map(channel => (
                    <div
                      key={channel.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ fontSize: '24px' }}>
                              {channel.type === 'email' ? '📧' : channel.type === 'slack' ? '💬' : channel.type === 'webhook' ? '🔗' : '📱'}
                            </span>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>{channel.name}</h3>
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                            Type: {channel.type}
                          </div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: channel.enabled ? '#28a74520' : '#dc354520',
                            color: channel.enabled ? '#28a745' : '#dc3545'
                          }}>
                            {channel.enabled ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteChannel(channel.id)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #dc3545',
                            background: 'white',
                            color: '#dc3545',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {channels.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px', gridColumn: '1 / -1' }}>
                      No notification channels configured. Add a channel to receive alerts.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Alert History</h2>
                <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Event</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Rule</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Severity</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Timestamp</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((event, index) => (
                        <tr key={index} style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: event.event_type === 'triggered' ? '#dc354520' : event.event_type === 'resolved' ? '#28a74520' : '#ffc10720',
                              color: event.event_type === 'triggered' ? '#dc3545' : event.event_type === 'resolved' ? '#28a745' : '#ffc107'
                            }}>
                              {event.event_type}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px' }}>{event.rule_name}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                            <span style={{ color: getSeverityColor(event.severity) }}>{event.severity}</span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#64748b' }}>
                            {new Date(event.timestamp).toLocaleString()}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', color: '#64748b' }}>
                            {event.details || '-'}
                          </td>
                        </tr>
                      ))}
                      {history.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                            No alert history yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Rule Modal */}
        {showRuleModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>
                {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
              </h2>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Rule Name</label>
                  <input
                    type="text"
                    value={ruleForm.name}
                    onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., High Revenue Alert"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Description</label>
                  <textarea
                    value={ruleForm.description}
                    onChange={e => setRuleForm({ ...ruleForm, description: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      minHeight: '60px'
                    }}
                    placeholder="Describe what this rule monitors..."
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 500 }}>Conditions</label>
                    <button
                      onClick={addCondition}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid #4A90E2',
                        background: 'white',
                        color: '#4A90E2',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      + Add Condition
                    </button>
                  </div>
                  {ruleForm.conditions.map((condition, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <select
                        value={condition.metric}
                        onChange={e => updateCondition(index, 'metric', e.target.value)}
                        style={{ flex: 1, padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      >
                        {metrics.map(m => (
                          <option key={m.name} value={m.name}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={condition.operator}
                        onChange={e => updateCondition(index, 'operator', e.target.value)}
                        style={{ width: '140px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      >
                        {operators.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={condition.threshold}
                        onChange={e => updateCondition(index, 'threshold', parseFloat(e.target.value))}
                        style={{ width: '100px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      />
                      {ruleForm.conditions.length > 1 && (
                        <button
                          onClick={() => removeCondition(index)}
                          style={{
                            padding: '8px',
                            border: 'none',
                            background: '#dc354520',
                            color: '#dc3545',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          X
                        </button>
                      )}
                    </div>
                  ))}
                  {ruleForm.conditions.length > 1 && (
                    <select
                      value={ruleForm.condition_logic}
                      onChange={e => setRuleForm({ ...ruleForm, condition_logic: e.target.value })}
                      style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', marginTop: '8px' }}
                    >
                      <option value="AND">All conditions must be true (AND)</option>
                      <option value="OR">Any condition can be true (OR)</option>
                    </select>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Severity</label>
                    <select
                      value={ruleForm.severity}
                      onChange={e => setRuleForm({ ...ruleForm, severity: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Cooldown (minutes)</label>
                    <input
                      type="number"
                      value={ruleForm.cooldown_minutes}
                      onChange={e => setRuleForm({ ...ruleForm, cooldown_minutes: parseInt(e.target.value) })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Notification Channels</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {channels.map(channel => (
                      <label key={channel.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={ruleForm.notification_channels.includes(channel.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setRuleForm({ ...ruleForm, notification_channels: [...ruleForm.notification_channels, channel.id] })
                            } else {
                              setRuleForm({ ...ruleForm, notification_channels: ruleForm.notification_channels.filter(id => id !== channel.id) })
                            }
                          }}
                        />
                        {channel.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowRuleModal(false); setEditingRule(null); }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={editingRule ? handleUpdateRule : handleCreateRule}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: '#4A90E2',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Channel Modal */}
        {showChannelModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '450px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>Add Notification Channel</h2>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Channel Name</label>
                  <input
                    type="text"
                    value={channelForm.name}
                    onChange={e => setChannelForm({ ...channelForm, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., Team Email"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Channel Type</label>
                  <select
                    value={channelForm.type}
                    onChange={e => setChannelForm({ ...channelForm, type: e.target.value, config: {} })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  >
                    <option value="email">Email</option>
                    <option value="slack">Slack</option>
                    <option value="webhook">Webhook</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>

                {channelForm.type === 'email' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Email Address</label>
                    <input
                      type="email"
                      value={channelForm.config.email || ''}
                      onChange={e => setChannelForm({ ...channelForm, config: { ...channelForm.config, email: e.target.value } })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="alerts@company.com"
                    />
                  </div>
                )}

                {channelForm.type === 'webhook' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Webhook URL</label>
                    <input
                      type="url"
                      value={channelForm.config.url || ''}
                      onChange={e => setChannelForm({ ...channelForm, config: { ...channelForm.config, url: e.target.value } })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="https://api.example.com/webhook"
                    />
                  </div>
                )}

                {channelForm.type === 'slack' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Slack Webhook URL</label>
                    <input
                      type="url"
                      value={channelForm.config.webhook_url || ''}
                      onChange={e => setChannelForm({ ...channelForm, config: { ...channelForm.config, webhook_url: e.target.value } })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      placeholder="https://hooks.slack.com/services/..."
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowChannelModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateChannel}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: '#4A90E2',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Add Channel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default AlertRulesPage
